import { Stack, Typography, useTheme } from "@mui/material";
import type { Control } from "react-hook-form";
import {
	ClientPropertyBuildingDispositionEnum,
	ClientPropertyBuildingMaterialEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import type {
	BuildingFormInput,
	BuildingFormOutput,
} from "web_myplann/Portfolio/Forms/BuildingShared/buildingFormSchema";
import { BUILDING_REQUIRED_MINIMUM_FIELDS } from "web_myplann/Portfolio/Forms/BuildingShared/buildingFormSchema";
import { RequiredMinimumMessage } from "web_myplann/Portfolio/PortfolioShared/RequiredMinimumMessage";
import { FlatRowFormDatePicker } from "web_myplann/Shared/Components/FlatRow/FlatRowFormDatePicker";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";
import { FlatRowFormSelect } from "web_myplann/Shared/Components/FlatRow/FlatRowFormSelect";
import { FlatRowFormSwitch } from "web_myplann/Shared/Components/FlatRow/FlatRowFormSwitch";
import { FlatRowFormTextarea } from "web_myplann/Shared/Components/FlatRow/FlatRowFormTextarea";
import { useEnumSelectOptions } from "web_myplann/Shared/Hooks/useEnumSelectOptions";
import { DateView } from "web_myplann/Shared/Types/DateView";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";
import { UNITS } from "web_myplann/Shared/Utils/units";

type BuildingFormFieldsProps = {
	control: Control<BuildingFormInput, unknown, BuildingFormOutput>;
	descriptionLabel: string;
};

const PropertyResources = Resources.PortfolioDetail.Property;
const FormResources = Resources.Portfolio.AddRealEstate;

export const BuildingFormFields = ({
	control,
	descriptionLabel,
}: BuildingFormFieldsProps) => {
	const theme = useTheme();
	const { t } = useResource();

	const dispositionOptions = useEnumSelectOptions(
		ClientPropertyBuildingDispositionEnum,
		PropertyResources.DispositionValue,
		[ClientPropertyBuildingDispositionEnum.None],
	);
	const buildingMaterialOptions = useEnumSelectOptions(
		ClientPropertyBuildingMaterialEnum,
		PropertyResources.BuildingMaterialValue,
		[
			ClientPropertyBuildingMaterialEnum.None,
			ClientPropertyBuildingMaterialEnum.Empty,
		],
	);

	return (
		<>
			<Typography
				variant="overline"
				display="block"
				pt={2.4}
				px={theme.spacingTokens.lg}
				pb={theme.spacingTokens.sm}
			>
				{t(FormResources.Sections.location)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormInput
					control={control}
					name="location"
					label={t(PropertyResources.StreetAndNumber)}
					placeholder={t(FormResources.Placeholders.optional)}
				/>
				<FlatRowFormInput
					control={control}
					name="locationMunicipality"
					label={t(PropertyResources.Municipality)}
					placeholder={t(FormResources.Placeholders.optional)}
				/>
				<FlatRowFormInput
					control={control}
					name="locationZipCode"
					label={t(PropertyResources.ZipCode)}
					placeholder={t(FormResources.Placeholders.optional)}
					isLast
				/>
			</Stack>
			<RequiredMinimumMessage
				control={control}
				fields={BUILDING_REQUIRED_MINIMUM_FIELDS}
				message={t(
					Resources.Portfolio.AddRealEstate.Validations.requiredMinimum,
				)}
			/>

			<Typography
				variant="overline"
				display="block"
				pt={2.4}
				px={theme.spacingTokens.lg}
				pb={theme.spacingTokens.sm}
			>
				{t(FormResources.Sections.parameters)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormSelect
					control={control}
					name="disposition"
					label={t(PropertyResources.Disposition)}
					options={dispositionOptions}
				/>
				<FlatRowFormSelect
					control={control}
					name="buildingMaterial"
					label={t(PropertyResources.BuildingMaterial)}
					options={buildingMaterialOptions}
				/>
				<FlatRowFormInput
					control={control}
					name="numberOfFloors"
					type="number"
					label={t(PropertyResources.NumberOfFloors)}
					placeholder={t(FormResources.Placeholders.optional)}
				/>
				<FlatRowFormInput
					control={control}
					name="usableArea"
					type="number"
					label={t(PropertyResources.UsableArea)}
					placeholder={t(FormResources.Placeholders.optional)}
					suffix={UNITS.squareMeter}
				/>
				<FlatRowFormInput
					control={control}
					name="floorArea"
					type="number"
					label={t(PropertyResources.FloorArea)}
					placeholder={t(FormResources.Placeholders.optional)}
					suffix={UNITS.squareMeter}
					isLast
				/>
			</Stack>

			<Typography
				variant="overline"
				display="block"
				pt={2.4}
				px={theme.spacingTokens.lg}
				pb={theme.spacingTokens.sm}
			>
				{t(FormResources.Sections.amenities)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormSwitch
					redesigned
					control={control}
					name="terrace"
					label={t(PropertyResources.Terrace)}
				/>
				<FlatRowFormSwitch
					redesigned
					control={control}
					name="loggia"
					label={t(PropertyResources.Loggia)}
				/>
				<FlatRowFormSwitch
					redesigned
					control={control}
					name="balcony"
					label={t(PropertyResources.Balcony)}
				/>
				<FlatRowFormSwitch
					redesigned
					control={control}
					name="garage"
					label={t(PropertyResources.Garage)}
				/>
				<FlatRowFormSwitch
					redesigned
					control={control}
					name="cellar"
					label={t(PropertyResources.Cellar)}
				/>
				<FlatRowFormSwitch
					redesigned
					control={control}
					name="elevator"
					label={t(PropertyResources.Elevator)}
				/>
				<FlatRowFormSwitch
					redesigned
					control={control}
					name="barrierFree"
					label={t(PropertyResources.BarrierFree)}
				/>
				<FlatRowFormSwitch
					redesigned
					control={control}
					name="swimmingPool"
					label={t(PropertyResources.SwimmingPool)}
					isLast
				/>
			</Stack>

			<Typography
				variant="overline"
				display="block"
				pt={2.4}
				px={theme.spacingTokens.lg}
				pb={theme.spacingTokens.sm}
			>
				{t(FormResources.Sections.valueAndDates)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormInput
					control={control}
					name="interiorValue"
					type="number"
					label={t(PropertyResources.InteriorPrice)}
					placeholder={t(FormResources.Placeholders.optional)}
					suffix="Kč"
				/>
				<FlatRowFormDatePicker
					control={control}
					name="purchaseDate"
					label={t(PropertyResources.PurchaseDate)}
					views={[DateView.Year, DateView.Month, DateView.Day]}
					displayFormat={DateFormat.Display}
					isLast
				/>
			</Stack>

			<Typography
				variant="overline"
				display="block"
				pt={2.4}
				px={theme.spacingTokens.lg}
				pb={theme.spacingTokens.sm}
			>
				{t(FormResources.Sections.description)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormTextarea
					control={control}
					name="description"
					label={descriptionLabel}
					placeholder={t(FormResources.Placeholders.optional)}
					rows={1}
					isLast
				/>
			</Stack>

			<Typography
				variant="overline"
				display="block"
				pt={2.4}
				px={theme.spacingTokens.lg}
				pb={theme.spacingTokens.sm}
			>
				{t(Resources.Portfolio.AddArt.Sections.investment)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormSwitch
					redesigned
					control={control}
					name="forInvestments"
					label={t(Resources.Portfolio.AddArt.Fields.forInvestments)}
					isLast
				/>
			</Stack>
		</>
	);
};
